import React from 'react';
import MoreLink from 'components/MoreLink';
import Skeleton from 'components/Common/Skeleton';
import ActionCard, { ActionCardVariant } from 'components/Common/Cards/ActionCard';
import { useNavigate } from 'hooks/useNavigate';
import { RoutePath } from 'enums/Routes';
import { FROM_FINANCIAL_DASHBOARD_STATE } from 'utils/navigationStates';

import { isMonetaryGoal, useGoal } from './useGoal';
import GoalCard from './GoalCard';

import styles from './GoalsSection.module.scss';

const AI_COACH_CHANGE_GOAL_EVENT = 'ai_coach_change_goal';

const GoalCardSkeleton = () => (
  <ActionCard variant={ActionCardVariant.White}>
    <div className={styles.skeletonCardContent}>
      <Skeleton width="180px" height="22px" mb="24px" />
      <div className={styles.skeletonProgressSection}>
        <Skeleton width="120px" height="120px" borderRadius="50%" />
        <div className={styles.skeletonAmountSection}>
          <Skeleton width="150px" height="32px" mb="8px" />
          <Skeleton width="120px" height="20px" mb="12px" />
          <Skeleton width="140px" height="24px" />
        </div>
      </div>
      <Skeleton width="100%" height="1px" my="16px" />
      <Skeleton width="160px" height="20px" />
    </div>
  </ActionCard>
);

const GoalLabelSkeleton = () => (
  <>
    <Skeleton width="60px" height="27px" />
    <Skeleton width="100px" height="16px" ml="auto" />
  </>
);

const GoalsSection: React.FC = () => {
  const { goal, isError, isLoading } = useGoal();
  const navigate = useNavigate();

  if (isError || (!goal && !isLoading)) return null;

  const handleChangeGoal = () => {
    analytics.track(AI_COACH_CHANGE_GOAL_EVENT);
    navigate(RoutePath.AICoachGoals, FROM_FINANCIAL_DASHBOARD_STATE);
  };

  // TODO: Implement the secondary label text for credit score (pl-2474)
  // Secondary label text for credit score should be "Checked daily"
  // But we cannot implement it now because we don't have equifax integration yet
  const secondaryLabelText = goal && isMonetaryGoal(goal) ? `${goal.percentage}% complete` : undefined;

  const showSkeleton = isLoading && !goal;

  return (
    <div className={styles.goalsSection}>
      <div className={styles.labelContainer}>
        {showSkeleton && <GoalLabelSkeleton />}
        {goal && (
          <>
            <p className={styles.primaryLabel}>Goal</p>
            {secondaryLabelText && <p className={styles.secondaryLabel}>{secondaryLabelText}</p>}
            <MoreLink
              onClick={handleChangeGoal}
              text="Change goal"
              showChevron={false}
              className={styles.changeGoalLink}
            />
          </>
        )}
      </div>
      {showSkeleton && <GoalCardSkeleton />}
      {goal && <GoalCard goal={goal} />}
    </div>
  );
};

export default GoalsSection;
